// Hand-rolled bridge from specs/seeds/dashboard-v2-settings/sections.yaml
// (CHG-1224) → typed TS constant.
//
// Per feedback_deterministic_codegen_over_hand_rolling.md: this file is a
// documented hand-rolled exception. The v2 FE generator (CHG-tracked TBD)
// will emit this file from the YAML automatically; until then the
// Settings shell (CHG-1239) consumes this constant directly. If the YAML
// changes, regenerate this file by hand and increment the source_hash
// comment below.
//
// Source: specs/seeds/dashboard-v2-settings/sections.yaml @ ITER-3346 / CHG-1224

import type { SettingsSection } from "../lib/settings-sections";

export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    section_id: "organization",
    label: "Organization",
    icon: "Building2",
    description: "Core organization profile — name, contact, address, mission.",
    route: "?section=organization",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "identity",
        label: "Identity",
        fields: [
          { field_id: "name", label: "Organization name", field_type: "text", pending_primitive: true, required: true },
          { field_id: "legal_name", label: "Legal name", field_type: "text", pending_primitive: true },
          { field_id: "short_name", label: "Short name", field_type: "text", pending_primitive: true, hint: "e.g., ACME" },
          { field_id: "org_category", label: "Category", field_type: "select", pending_primitive: true, options_source: "org_category_value_space" },
        ],
      },
      {
        group_id: "contact",
        label: "Contact",
        fields: [
          { field_id: "contact_email", label: "Contact email", field_type: "email" },
          { field_id: "phone", label: "Phone", field_type: "phone" },
          { field_id: "website_url", label: "Website URL", field_type: "url", pending_primitive: true },
        ],
      },
      {
        group_id: "address",
        label: "Address",
        fields: [
          { field_id: "address_line1", label: "Address line 1", field_type: "text", pending_primitive: true },
          { field_id: "address_line2", label: "Address line 2", field_type: "text", pending_primitive: true },
          { field_id: "city", label: "City", field_type: "text", pending_primitive: true },
          { field_id: "state_province", label: "State / Province", field_type: "text", pending_primitive: true },
          { field_id: "postal_code", label: "Postal code", field_type: "text", pending_primitive: true },
          { field_id: "country", label: "Country", field_type: "country_picker" },
        ],
      },
      {
        group_id: "mission",
        label: "Mission",
        fields: [
          { field_id: "mission_statement", label: "Mission statement", field_type: "textarea", pending_primitive: true, max_length: 500 },
        ],
      },
    ],
  },
  {
    section_id: "brand_identity",
    label: "Brand identity",
    icon: "Palette",
    description: "Logo, brand colors, public slug, and donor-facing brand kit.",
    route: "?section=brand_identity",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "logo_and_assets",
        label: "Logo and assets",
        fields: [
          { field_id: "logo_primary", label: "Primary logo", field_type: "file_upload", accept: "image/*" },
          { field_id: "logo_compact", label: "Compact logo / mark", field_type: "file_upload", accept: "image/*" },
          { field_id: "favicon", label: "Favicon", field_type: "file_upload", accept: "image/*" },
        ],
      },
      {
        group_id: "colors",
        label: "Brand colors",
        fields: [
          { field_id: "primary_color", label: "Primary color", field_type: "color" },
          { field_id: "accent_color", label: "Accent color", field_type: "color" },
          { field_id: "text_color", label: "Text color", field_type: "color" },
        ],
      },
      {
        group_id: "public_slug",
        label: "Public URL",
        fields: [
          { field_id: "public_slug", label: "Public slug", field_type: "text", pending_primitive: true, hint: "giving.at/{slug}" },
        ],
      },
    ],
  },
  {
    section_id: "localization",
    label: "Localization",
    icon: "Globe",
    description: "Currency, locale, timezone, and date / number formatting defaults.",
    route: "?section=localization",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "regional_defaults",
        label: "Regional defaults",
        fields: [
          { field_id: "default_currency", label: "Default currency", field_type: "currency_picker", required: true },
          { field_id: "default_locale", label: "Default locale", field_type: "locale_picker", required: true },
          { field_id: "default_timezone", label: "Default timezone", field_type: "timezone_picker", required: true },
        ],
      },
      {
        group_id: "formatting",
        label: "Formatting preferences",
        fields: [
          { field_id: "date_format", label: "Date format", field_type: "select", pending_primitive: true, options_source: "date_format_value_space" },
          { field_id: "number_format", label: "Number format", field_type: "select", pending_primitive: true, options_source: "number_format_value_space" },
        ],
      },
    ],
  },
  {
    section_id: "channel_defaults",
    label: "Channel defaults",
    icon: "Layers",
    description: "Default designations, amounts, and behavior across the five giving channels.",
    route: "?section=channel_defaults",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "default_designation",
        label: "Default designation",
        fields: [
          { field_id: "default_fund", label: "Default fund / designation", field_type: "designation_picker" },
          { field_id: "default_target", label: "Default donation target", field_type: "donation_target_picker" },
        ],
      },
      {
        group_id: "suggested_amounts",
        label: "Suggested amounts",
        fields: [
          { field_id: "amount_one", label: "Suggested amount 1", field_type: "amount" },
          { field_id: "amount_two", label: "Suggested amount 2", field_type: "amount" },
          { field_id: "amount_three", label: "Suggested amount 3", field_type: "amount" },
          { field_id: "amount_four", label: "Suggested amount 4", field_type: "amount" },
        ],
      },
      {
        group_id: "channel_toggles",
        label: "Per-channel toggles",
        fields: [
          { field_id: "enable_widget", label: "Enable embeddable widget", field_type: "checkbox", pending_primitive: true },
          { field_id: "enable_donate_page", label: "Enable public donate page", field_type: "checkbox", pending_primitive: true },
          { field_id: "enable_kiosk", label: "Enable kiosk channel", field_type: "checkbox", pending_primitive: true },
          { field_id: "enable_mobile", label: "Enable mobile app channel", field_type: "checkbox", pending_primitive: true },
        ],
      },
    ],
  },
  {
    section_id: "security_access",
    label: "Security and access",
    icon: "Shield",
    description: "Authentication policy, MFA enforcement, SSO providers, and session controls.",
    route: "?section=security_access",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "authentication",
        label: "Authentication",
        fields: [
          { field_id: "require_mfa", label: "Require MFA for staff", field_type: "checkbox", pending_primitive: true },
          { field_id: "allowed_mfa_methods", label: "Allowed MFA methods", field_type: "tag_input", options_source: "mfa_method_value_space" },
          { field_id: "session_timeout_minutes", label: "Session timeout (minutes)", field_type: "number", pending_primitive: true, min: 5, max: 1440 },
        ],
      },
      {
        group_id: "sso",
        label: "Single sign-on",
        fields: [
          { field_id: "sso_google_enabled", label: "Google SSO", field_type: "checkbox", pending_primitive: true },
          { field_id: "sso_facebook_enabled", label: "Facebook SSO", field_type: "checkbox", pending_primitive: true },
          { field_id: "sso_linkedin_enabled", label: "LinkedIn SSO", field_type: "checkbox", pending_primitive: true },
          { field_id: "sso_apple_enabled", label: "Apple SSO", field_type: "checkbox", pending_primitive: true },
        ],
      },
      {
        group_id: "ip_policy",
        label: "IP allowlist",
        fields: [
          { field_id: "admin_ip_allowlist", label: "Admin IP allowlist", field_type: "tag_input", hint: "CIDR blocks; empty = no restriction" },
        ],
      },
    ],
  },
  {
    section_id: "compliance",
    label: "Compliance",
    icon: "Scale",
    description: "Receipts, tax IDs, data-retention policy, and audit posture.",
    route: "?section=compliance",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "tax_identity",
        label: "Tax identity",
        fields: [
          { field_id: "tax_id", label: "Tax / EIN", field_type: "text", pending_primitive: true },
          { field_id: "tax_id_country", label: "Tax country", field_type: "country_picker" },
          { field_id: "charitable_status", label: "Charitable status", field_type: "select", pending_primitive: true, options_source: "charitable_status_value_space" },
        ],
      },
      {
        group_id: "receipts",
        label: "Donation receipts",
        fields: [
          { field_id: "auto_receipt_enabled", label: "Auto-send receipt on donation", field_type: "checkbox", pending_primitive: true },
          { field_id: "receipt_threshold", label: "Receipt threshold", field_type: "amount", hint: "Minimum gift amount that triggers an auto-receipt" },
          { field_id: "receipt_template_id", label: "Receipt template", field_type: "select", pending_primitive: true, options_source: "notification_template_registry" },
        ],
      },
      {
        group_id: "data_retention",
        label: "Data retention",
        fields: [
          { field_id: "donor_pii_retention_days", label: "Donor PII retention (days)", field_type: "number", pending_primitive: true, min: 30, max: 36500 },
          { field_id: "archive_after_days", label: "Archive lapsed donors after (days)", field_type: "number", pending_primitive: true, min: 90, max: 3650 },
        ],
      },
    ],
  },
  {
    section_id: "intelligence",
    label: "Intelligence",
    icon: "Sparkles",
    description: "AI assistance, automation rules, and lifecycle classifier thresholds.",
    route: "?section=intelligence",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "ai_assist",
        label: "AI assistance",
        fields: [
          { field_id: "ai_writing_enabled", label: "Enable AI writing suggestions", field_type: "checkbox", pending_primitive: true },
          { field_id: "ai_persona", label: "Default brand voice", field_type: "select", pending_primitive: true, options_source: "ai_persona_value_space" },
          { field_id: "ai_autopilot_enabled", label: "Enable AI autopilot", field_type: "checkbox", pending_primitive: true },
        ],
      },
      {
        group_id: "lifecycle_thresholds",
        label: "Donor lifecycle thresholds",
        fields: [
          { field_id: "at_risk_days", label: "At-risk threshold (days)", field_type: "number", pending_primitive: true, min: 30, max: 365 },
          { field_id: "lapsed_days", label: "Lapsed threshold (days)", field_type: "number", pending_primitive: true, min: 180, max: 730 },
          { field_id: "reactivation_window_days", label: "Reactivation window (days)", field_type: "number", pending_primitive: true, min: 30, max: 365 },
          { field_id: "vip_amount", label: "VIP lifetime giving threshold", field_type: "amount" },
          { field_id: "vip_donation_count", label: "VIP lifetime gift count", field_type: "number", pending_primitive: true, min: 1, max: 1000 },
        ],
      },
      {
        group_id: "automation_rules",
        label: "Automation rules",
        fields: [
          { field_id: "automation_enabled", label: "Enable lifecycle automations", field_type: "checkbox", pending_primitive: true },
        ],
      },
    ],
  },
  {
    section_id: "integrations",
    label: "Integrations",
    icon: "Plug",
    description: "Payment providers, email / SMS providers, webhooks, and outbound API keys.",
    route: "?section=integrations",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "payment_providers",
        label: "Payment providers",
        fields: [
          { field_id: "stripe_account_id", label: "Stripe account ID", field_type: "text", pending_primitive: true, masked: true },
          { field_id: "paypal_account_id", label: "PayPal account ID", field_type: "text", pending_primitive: true, masked: true },
          { field_id: "default_payment_provider", label: "Default payment provider", field_type: "select", pending_primitive: true, options_source: "payment_provider_registry" },
        ],
      },
      {
        group_id: "messaging_providers",
        label: "Messaging providers",
        fields: [
          { field_id: "email_provider", label: "Email provider", field_type: "select", pending_primitive: true, options_source: "email_provider_registry" },
          { field_id: "smtp_endpoint", label: "SMTP endpoint", field_type: "url", pending_primitive: true },
          { field_id: "sms_provider", label: "SMS provider", field_type: "select", pending_primitive: true, options_source: "sms_provider_registry" },
        ],
      },
      {
        group_id: "webhooks",
        label: "Outbound webhooks",
        fields: [
          { field_id: "webhook_endpoints", label: "Webhook endpoints", field_type: "tag_input", hint: "One URL per entry" },
        ],
      },
    ],
  },
  {
    section_id: "billing",
    label: "Billing",
    icon: "CreditCard",
    description: "Platform-billing plan, payment method, invoice history, and tax info.",
    route: "?section=billing",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "plan",
        label: "Plan",
        fields: [
          { field_id: "plan_tier", label: "Plan tier", field_type: "select", pending_primitive: true, options_source: "plan_tier_value_space", readonly: true },
          { field_id: "billing_cycle", label: "Billing cycle", field_type: "select", pending_primitive: true, options_source: "billing_cycle_value_space" },
        ],
      },
      {
        group_id: "payment_method",
        label: "Payment method on file",
        fields: [
          { field_id: "card_last_four", label: "Card on file", field_type: "text", pending_primitive: true, readonly: true },
          { field_id: "billing_email", label: "Billing email", field_type: "email" },
        ],
      },
      {
        group_id: "tax",
        label: "Tax",
        fields: [
          { field_id: "billing_tax_id", label: "Tax ID for invoicing", field_type: "text", pending_primitive: true },
          { field_id: "billing_country", label: "Billing country", field_type: "country_picker" },
        ],
      },
    ],
  },
  {
    section_id: "templates",
    label: "Notification templates",
    icon: "FileText",
    description: "Email / SMS layout selection and per-template overrides.",
    route: "?section=templates",
    save_permission: "alora-giving.notification-template.update",
    field_groups: [
      {
        group_id: "layout",
        label: "Default layout",
        fields: [
          { field_id: "default_email_layout", label: "Default email layout", field_type: "select", pending_primitive: true, options_source: "notification_layout_registry" },
          { field_id: "header_html", label: "Header HTML override", field_type: "rich_text" },
          { field_id: "footer_html", label: "Footer HTML override", field_type: "rich_text" },
        ],
      },
      {
        group_id: "from_identity",
        label: "From identity",
        fields: [
          { field_id: "from_name", label: "From name", field_type: "text", pending_primitive: true },
          { field_id: "from_email", label: "From email", field_type: "email" },
          { field_id: "reply_to_email", label: "Reply-to email", field_type: "email" },
        ],
      },
    ],
  },
  {
    section_id: "advanced",
    label: "Advanced",
    icon: "Wrench",
    description: "Feature flags, developer tooling, data export, and danger-zone actions.",
    route: "?section=advanced",
    save_permission: "alora-giving.organization.update",
    field_groups: [
      {
        group_id: "feature_flags",
        label: "Feature flags",
        fields: [
          { field_id: "enable_beta_features", label: "Enable beta features", field_type: "checkbox", pending_primitive: true },
          { field_id: "enabled_flag_keys", label: "Enabled flag keys", field_type: "tag_input" },
        ],
      },
      {
        group_id: "developer",
        label: "Developer tooling",
        fields: [
          { field_id: "api_key_id", label: "API key (read-only)", field_type: "text", pending_primitive: true, masked: true, readonly: true },
          { field_id: "webhook_signing_secret", label: "Webhook signing secret", field_type: "text", pending_primitive: true, masked: true },
        ],
      },
      {
        group_id: "data_export",
        label: "Data export",
        fields: [
          { field_id: "export_schedule", label: "Export schedule", field_type: "schedule_picker" },
          { field_id: "export_destination_url", label: "Export destination URL", field_type: "url", pending_primitive: true },
        ],
      },
      {
        group_id: "danger_zone",
        label: "Danger zone",
        fields: [
          { field_id: "archive_organization", label: "Archive organization", field_type: "checkbox", pending_primitive: true, danger: true },
        ],
      },
    ],
  },
];
